import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import moment from 'moment';
import { Inline, List, ListItem } from 'chaoskit/src/components';
import { isExternalUrl } from 'chaoskit/src/helpers/utility';

import { Data, Link } from '../../components';
import FooterCTA from './FooterCTA';
import NewsletterForm from './NewsletterForm';
import SocialGroup from '../global/SocialGroup';
import techCrunch from '../../assets/media/logo-techcrunch.svg';
import forbes from '../../assets/media/logo-forbes.svg';
import ventureBeat from '../../assets/media/logo-venturebeat.svg';
import businessInsider from '../../assets/media/logo-business-insider.svg';

const Footer = ({ hideCTA }) => {
  const {
    file: { childDataYaml: pageData },
  } = useStaticQuery(graphql`
    query FooterPageData {
      file(name: { eq: "footer" }) {
        childDataYaml {
          linkGroups {
            heading
            links {
              label
              url
            }
          }
          social {
            title
            service
            url
          }
        }
      }
    }
  `);

  return (
    <Fragment>
      {!hideCTA && <FooterCTA />}
      <footer className="section footer u-contrast">
        <div className="container">
          <Inline className="footer__logos u-flexBetween" size="medium">
            <a
              href="https://techcrunch.com/2018/02/04/the-rise-of-chaos-engineering/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img style={{ height: 22 }} src={techCrunch} alt="TechCrunch" />
            </a>
            <a
              href="https://www.forbes.com/sites/forbestechcouncil/2018/04/26/why-ctos-and-cios-should-care-more-about-the-cost-of-downtime/#272f69eb131c"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img style={{ height: 26 }} src={forbes} alt="Forbes" />
            </a>
            <a
              href="https://www.businessinsider.com/gremlin-free-chaos-engineering-netflix-amazon-2019-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                style={{ height: 16 }}
                src={businessInsider}
                alt="Business Insider"
              />
            </a>
            <a
              href="https://venturebeat.com/2017/12/12/gremlin-launches-to-help-companies-stress-test-their-apps/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img style={{ height: 22 }} src={ventureBeat} alt="VentureBeat" />
            </a>
          </Inline>
          <hr className="footer-hr" />
          <div className="row row--deepMatch row--largeGutter">
            <div className="column-6@medium">
              <div>
                <Inline className="footer__metaLinks u-flexTop" size="large">
                  {pageData.linkGroups.map(linkGroup => (
                    <div key={linkGroup.heading}>
                      <h5 className="u-textMuted u-mb--medium">
                        <Data>{linkGroup.heading}</Data>
                      </h5>
                      <List type={['space']}>
                        {linkGroup.links.map(link => {
                          const newWindowProps = link.newTab && {
                            target: '_blank',
                            rel: 'noopener noreferrer',
                          };

                          return (
                            <ListItem key={link.label}>
                              {isExternalUrl(link.url) ? (
                                <a
                                  className="u-link"
                                  href={link.url}
                                  {...newWindowProps}
                                >
                                  {link.label}
                                </a>
                              ) : (
                                <Link
                                  className="u-link"
                                  to={link.url}
                                  {...newWindowProps}
                                >
                                  {link.label}
                                </Link>
                              )}
                            </ListItem>
                          );
                        })}
                      </List>
                    </div>
                  ))}
                </Inline>
              </div>
            </div>
            <div className="column-6@medium">
              <div>
                <div className="footer__subscribe">
                  <h4>Subscribe to our newsletter</h4>
                  <NewsletterForm />
                </div>
                <SocialGroup
                  className="footer__social"
                  networks={pageData.social}
                />
              </div>
            </div>
          </div>
          <hr className="footer-hr" />
          <div className="u-textCenter u-textSmall">
            &copy; {moment().format('Y')} Gremlin Inc. San Jose, CA 95113
          </div>
        </div>
      </footer>
    </Fragment>
  );
};

Footer.propTypes = {
  hideCTA: PropTypes.bool,
};

export default Footer;
